import React from "react";
import { Badge, Box, Button, ButtonGroup, Heading, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { OktonAlert, OktonLoadingState, OktonPanel } from "@okton/platform-ui";
import { BenchmarkResult, loadRouterCatalog, RouterCatalog, runBenchmark } from "../ai-provider/aiRouterApi";
import {
  LearningAnswer,
  LearningCandidate,
  loadLearningAnswers,
  loadLearningCandidates,
  promoteLearningCandidate,
  retireLearningAnswer,
  reviewLearningCandidate,
} from "./aiLearningApi";

export const AiLearningPanel: React.FC<{ accessToken?: string | null }> = ({ accessToken }) => {
  const [data,setData]=React.useState<RouterCatalog|null>(null);
  const [candidates,setCandidates]=React.useState<LearningCandidate[]>([]);
  const [answers,setAnswers]=React.useState<LearningAnswer[]>([]);
  const [error,setError]=React.useState("");
  const [loading,setLoading]=React.useState(true);
  const [benchmarking,setBenchmarking]=React.useState(false);
  const [benchmark,setBenchmark]=React.useState<BenchmarkResult|null>(null);
  const [busyAction,setBusyAction]=React.useState("");

  async function loadAll(signal:AbortSignal){
    if(!accessToken)throw new Error("Sessão indisponível.");
    const [catalog,candidateRows,answerRows]=await Promise.all([
      loadRouterCatalog(accessToken,signal),
      loadLearningCandidates(accessToken,signal),
      loadLearningAnswers(accessToken,signal),
    ]);
    setData(catalog);
    setCandidates(candidateRows);
    setAnswers(answerRows);
  }

  React.useEffect(()=>{
    const controller=new AbortController();
    setLoading(true);
    setError("");
    loadAll(controller.signal)
      .catch(e=>{if(!controller.signal.aborted)setError(e instanceof Error?e.message:"Erro ao carregar.");})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return()=>controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[accessToken]);

  async function refresh(){
    const controller=new AbortController();
    await loadAll(controller.signal);
  }

  async function executeBenchmark(){
    if(!accessToken)return;
    const controller=new AbortController();
    setBenchmarking(true);
    setError("");
    try{
      const result=await runBenchmark(accessToken,controller.signal);
      setBenchmark(result);
      await refresh();
    }catch(e){
      setError(e instanceof Error?e.message:"Erro ao executar benchmark.");
    }finally{
      setBenchmarking(false);
    }
  }

  async function review(candidate:LearningCandidate,decision:"approved"|"rejected"){
    if(!accessToken)return;
    const key=candidate.id+":"+decision;
    setBusyAction(key);
    setError("");
    try{
      await reviewLearningCandidate(accessToken,candidate.id,decision);
      await refresh();
    }catch(e){
      setError(e instanceof Error?e.message:"Não foi possível revisar o candidato.");
    }finally{
      setBusyAction("");
    }
  }

  async function promote(candidate:LearningCandidate){
    if(!accessToken)return;
    const key=candidate.id+":promote";
    setBusyAction(key);
    setError("");
    try{
      await promoteLearningCandidate(accessToken,candidate.id);
      await refresh();
    }catch(e){
      setError(e instanceof Error?e.message:"Não foi possível promover o candidato.");
    }finally{
      setBusyAction("");
    }
  }

  async function retire(answer:LearningAnswer){
    if(!accessToken)return;
    const key=answer.id+":retire";
    setBusyAction(key);
    setError("");
    try{
      await retireLearningAnswer(accessToken,answer.id);
      await refresh();
    }catch(e){
      setError(e instanceof Error?e.message:"Não foi possível retirar a resposta.");
    }finally{
      setBusyAction("");
    }
  }

  if(loading)return <OktonLoadingState title="Carregando aprendizado..." minH="180px"/>;
  if(!data)return <OktonAlert status="error"><Text>{error||"Aprendizado indisponível."}</Text></OktonAlert>;

  const l=data.learning;
  return <Stack spacing="6">
    <OktonAlert status="info">
      <Text>Somente respostas aprovadas podem ser reutilizadas. Candidatos não entram no conhecimento global automaticamente.</Text>
    </OktonAlert>
    {error && <OktonAlert status="error"><Text>{error}</Text></OktonAlert>}

    <OktonPanel title="Answer Library" subtitle="Respostas reutilizáveis com escopo e versionamento">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Aprovadas</Heading><Text fontSize="2xl" fontWeight="700">{l.approved}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Rascunhos</Heading><Text fontSize="2xl" fontWeight="700">{l.draft}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Uso acumulado</Heading><Text fontSize="2xl" fontWeight="700">{l.usage}</Text></Box>
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Candidatos para revisão" subtitle="Perguntas e respostas já sanitizadas; promoção exige revisão explícita">
      {candidates.length===0 ? <Text fontSize="sm">Nenhum candidato disponível.</Text> :
        <Stack spacing="4">
          {candidates.map(candidate=><Box key={candidate.id} borderWidth="1px" rounded="md" p="4">
            <Stack spacing="2">
              <Stack direction={["column","row"]} justify="space-between">
                <Box>
                  <Heading size="xs">{candidate.systemId} · {candidate.candidateType}</Heading>
                  <Text fontSize="xs">{candidate.scopeType} · empresa {candidate.companyId||"-"} · filial {candidate.branchId||"-"}</Text>
                </Box>
                <Stack direction="row">
                  <Badge>{candidate.reviewStatus}</Badge>
                  <Badge>{candidate.piiStatus}</Badge>
                  <Badge>{candidate.confidence}%</Badge>
                </Stack>
              </Stack>
              {candidate.sanitizedQuestion && <Text fontSize="sm"><b>Pergunta:</b> {candidate.sanitizedQuestion}</Text>}
              {candidate.sanitizedAnswer && <Text fontSize="sm" whiteSpace="pre-wrap"><b>Resposta:</b> {candidate.sanitizedAnswer}</Text>}
              {candidate.reviewStatus==="pending" && <ButtonGroup size="sm">
                <Button
                  colorScheme="blue"
                  isDisabled={candidate.piiStatus==="blocked"}
                  isLoading={busyAction===candidate.id+":approved"}
                  onClick={()=>review(candidate,"approved")}
                >Aprovar</Button>
                <Button
                  variant="outline"
                  isLoading={busyAction===candidate.id+":rejected"}
                  onClick={()=>review(candidate,"rejected")}
                >Rejeitar</Button>
              </ButtonGroup>}
              {candidate.reviewStatus==="approved" && candidate.candidateType==="answer" && <Box>
                <Button
                  size="sm"
                  colorScheme="blue"
                  isLoading={busyAction===candidate.id+":promote"}
                  onClick={()=>promote(candidate)}
                >Promover para Answer Library</Button>
              </Box>}
            </Stack>
          </Box>)}
        </Stack>}
    </OktonPanel>

    <OktonPanel title="Respostas publicadas" subtitle="Conhecimento aprovado atualmente disponível para reutilização">
      {answers.length===0 ? <Text fontSize="sm">Nenhuma resposta cadastrada.</Text> :
        <Stack spacing="4">
          {answers.map(answer=><Box key={answer.id} borderWidth="1px" rounded="md" p="4">
            <Stack spacing="2">
              <Stack direction={["column","row"]} justify="space-between">
                <Box>
                  <Heading size="xs">{answer.systemId} · {answer.scopeType}</Heading>
                  <Text fontSize="xs">empresa {answer.companyId||"-"} · filial {answer.branchId||"-"} · uso {answer.usageCount}</Text>
                </Box>
                <Stack direction="row">
                  <Badge>{answer.status}</Badge>
                  <Badge>{answer.confidence}%</Badge>
                </Stack>
              </Stack>
              <Text fontSize="sm"><b>Pergunta:</b> {answer.question}</Text>
              <Text fontSize="sm" whiteSpace="pre-wrap"><b>Resposta:</b> {answer.answer}</Text>
              {answer.status==="approved" && <Box>
                <Button
                  size="sm"
                  variant="outline"
                  isLoading={busyAction===answer.id+":retire"}
                  onClick={()=>retire(answer)}
                >Retirar resposta</Button>
              </Box>}
            </Stack>
          </Box>)}
        </Stack>}
    </OktonPanel>

    <OktonPanel title="Intent Library" subtitle="Exemplos sanitizados usados somente como fallback de alta confiança do Tier 0">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Exemplos ativos</Heading><Text fontSize="2xl" fontWeight="700">{l.intents.active}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Intenções cobertas</Heading><Text fontSize="2xl" fontWeight="700">{l.intents.intents}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Sistemas</Heading><Text fontSize="2xl" fontWeight="700">{l.intents.systems}</Text></Box>
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Dataset de avaliação" subtitle="Casos versionados para medir qualidade, custo e latência antes de mudar o Router">
      <Stack spacing="4">
        <SimpleGrid columns={[1,2,3]} spacing="4">
          <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Casos ativos</Heading><Text fontSize="2xl" fontWeight="700">{l.evaluation.activeCases}</Text></Box>
          <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Execuções</Heading><Text fontSize="2xl" fontWeight="700">{l.evaluation.runs}</Text></Box>
          <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Score médio</Heading><Text fontSize="2xl" fontWeight="700">{l.evaluation.averageScore.toFixed(0)}</Text></Box>
        </SimpleGrid>
        <Box>
          <Button size="sm" colorScheme="blue" isLoading={benchmarking} onClick={executeBenchmark}>Executar benchmark dry-run</Button>
        </Box>
        {benchmark && <OktonAlert status={benchmark.failed===0?"success":"warning"}>
          <Text><b>Último benchmark:</b> {benchmark.passed}/{benchmark.total} aprovados · score médio {benchmark.averageScore.toFixed(0)} · {benchmark.failed} falhas · sem chamadas pagas.</Text>
        </OktonAlert>}
      </Stack>
    </OktonPanel>

    <OktonPanel title="Confiança evolutiva" subtitle="Feedback e testes podem rebaixar intenções que deixam de ser seguras para Tier 0">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Score médio</Heading><Text fontSize="2xl" fontWeight="700">{l.quality.average.toFixed(0)}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Seguras ≥ 90</Heading><Text fontSize="2xl" fontWeight="700">{l.quality.safe}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Precisam revisão</Heading><Text fontSize="2xl" fontWeight="700">{l.quality.needsReview}</Text></Box>
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Feedback" subtitle="Sinais de qualidade que alimentam a avaliação do conhecimento">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Positivos</Heading><Text fontSize="2xl" fontWeight="700">{l.feedback.positive}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Negativos</Heading><Text fontSize="2xl" fontWeight="700">{l.feedback.negative}</Text></Box>
        <Box borderWidth="1px" rounded="md" p="4"><Heading size="xs">Reformulações</Heading><Text fontSize="2xl" fontWeight="700">{l.feedback.rephrased}</Text></Box>
      </SimpleGrid>
    </OktonPanel>
  </Stack>;
};
